import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { InspectorsRepository } from "@/api/inspectors/repository";
import { InspectorsService } from "@/api/inspectors/service";
import type { IInspectorLicense } from "@/types/users-inspectors";
import { Toast } from "@/ui-kit";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

type ILicenseFormData = {
  licenseIdNumber: string;
  licenseIdIssuanceDate: string;
  licenseIdExpiryDate: string;
  licenseFrontPicture: File[];
  licenseFrontPictureUrl?: string;
  licenseBackPicture: File[];
  licenseBackPictureUrl?: string;
};

type IUseLicenseFormOptions = {
  userId: number;
  license?: IInspectorLicense;
  onSuccess: (response: IInspectorLicense) => void;
};

export const useLicenseForm = ({
  userId,
  license,
  onSuccess,
}: IUseLicenseFormOptions) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const schema = useMemo(() => {
    return z.object({
      licenseIdNumber: z.string().min(1, { message: t("validation.required") }),
      licenseIdIssuanceDate: z
        .string()
        .min(1, { message: t("validation.required") }),
      licenseIdExpiryDate: z
        .string()
        .min(1, { message: t("validation.required") }),
      licenseFrontPicture: z
        .array(z.instanceof(File))
        .refine((files) => files.length > 0, {
          message: t("validation.required"),
        }),
      licenseBackPicture: z
        .array(z.instanceof(File))
        .refine((files) => files.length > 0, {
          message: t("validation.required"),
        }),
    });
  }, [t]);

  const defaultValues = useMemo((): ILicenseFormData => {
    return {
      licenseIdNumber: license?.documentNumber?.toString() ?? "",
      licenseIdIssuanceDate: license?.issuanceDate ?? "",
      licenseIdExpiryDate: license?.expiryDate ?? "",
      licenseFrontPicture: [],
      licenseFrontPictureUrl: license?.frontImageUrl,
      licenseBackPicture: [],
      licenseBackPictureUrl: license?.backImageUrl,
    };
  }, [license]);

  const form = useForm<ILicenseFormData>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const { handleSubmit } = form;

  const onSubmit = useCallback(() => {
    handleSubmit((formData: ILicenseFormData) => {
      setLoading(true);
      const repository = new InspectorsRepository(new InspectorsService());

      repository
        .upsertLicenseDetails({
          license: {
            userId,
            issuanceDate: formData.licenseIdIssuanceDate,
            expiryDate: formData.licenseIdExpiryDate,
            documentNumber: formData.licenseIdNumber,
          },
          frontImage: formData.licenseFrontPicture[0],
          backImage: formData.licenseBackPicture[0],
        })
        .then((response) => {
          Toast.success({
            title: t("toast.licenseDetailsUpdated"),
          });
          onSuccess(response);
        })
        .catch(serverErrorHandler())
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [handleSubmit, userId, onSuccess, t]);

  return { form, onSubmit, loading };
};
